import type { Page, Workspace } from "@baton/api";
import { lastRouteKey, lastWorkspaceKey, validateRedirect, type Runtime } from "@baton/core";
import { useI18n } from "@baton/i18n";
import { Check, LogOut, Plus, UserPlus } from "@baton/icons";
import { Popover, Separator } from "@baton/ui";
import type { UseQueryResult } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import type { MeView } from "../../app/queries.ts";
import { signOut } from "../../auth/session/signOut.ts";
import { isWorkspaceAdmin } from "../state/permissions.ts";
import { NavCardRow } from "./NavCardRow.tsx";
import { TeamMark } from "./TeamMark.tsx";

/**
 * FR-015-005, `contracts/screens.md` § Sidebar, `spec.md` US1 scenario 4: the first row of the sidebar
 * is a card naming the current workspace beside its mark, and it opens a card listing every workspace
 * from `GET /workspaces` with the current one checked, then Create workspace, Invite people for an
 * owner or admin (opening the Invites page's dialog), and Sign out. Switching changes the URL prefix
 * and restores that workspace's last route from local storage. Workspace settings is not in this card,
 * because Settings is its own rail mode. The instance's workspace-creation policy is not in
 * `GET /instance`, so Create workspace is offered and the server's 403 on `POST /workspaces` is what
 * refuses it.
 *
 * The card is Spectrum UI's Nav List Card (`@spectrumui/nav-list-card`, Apache-2.0), themed through the
 * variables of `packages/tokens` (2026-09-15, by the owner's decision): the list's title over pill rows
 * on the source's spring (`NavCardRow.tsx`). What differs from the source, and why: the card opens from
 * the switcher in a popover rather than sitting in the page; a rule separates the workspaces from the
 * actions, which the source's one list has no second group for; and each workspace row leads with its
 * mark, so the rows read as the switcher card does. The rows are a list of buttons moved through with
 * Tab, as the source's are, where the earlier menu moved with the arrow keys.
 *
 * Every mark is the team's own colour (`TeamMark.tsx`, 2026-09-18), so the rows of the card tell the
 * teams apart; a row read from the memberships before the team list answers keeps the fixed token.
 */
export interface WorkspaceSwitcherProps {
  readonly runtime: Runtime;
  readonly ws: string;
  readonly me: MeView;
  readonly workspaces: UseQueryResult<Page<Workspace>>;
  readonly current: Workspace | undefined;
  readonly onNavigate?: () => void;
}

function lastRouteFor(runtime: Runtime, slug: string): string {
  const stored = validateRedirect(runtime.storage.get(lastRouteKey, slug));
  return stored?.startsWith(`/${slug}/`) === true ? stored : `/${slug}/dashboard`;
}

export function WorkspaceSwitcher({ runtime, ws, me, workspaces, current, onNavigate }: WorkspaceSwitcherProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const membership = me.memberships.find((m) => m.slug === ws);
  const name = current?.name ?? membership?.name ?? ws;
  const list: readonly { readonly slug: string; readonly name: string; readonly theme?: Workspace["theme"]; readonly logo_url?: string | null }[] = workspaces.data?.items ?? me.memberships.map((m) => ({ slug: m.slug, name: m.name }));
  const act = (run: () => void) => () => {
    setOpen(false);
    onNavigate?.();
    run();
  };
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="start"
      label={t("workspace.switcher")}
      className="w-(--radix-popover-trigger-width) px-0 pt-(--space-4) pb-(--space-2)"
      trigger={
        <button type="button" className="b-sidebar__switcher" aria-label={t("workspace.switcherFor", { name })}>
          <TeamMark name={name} theme={current?.theme} logoUrl={current?.logo_url} className="b-sidebar__switcher-mark" />
          <span className="b-sidebar__switcher-name">{name}</span>
          <span className="b-caret b-sidebar__switcher-caret" aria-hidden="true" />
        </button>
      }
    >
      <p className="m-0 px-(--space-5) text-caption font-medium text-text-tertiary">{t("workspace.list.title")}</p>
      <ul className="m-0 mt-(--space-2) flex list-none flex-col gap-(--space-1) px-(--space-2)" aria-label={t("workspace.list.title")}>
        {list.map((workspace) => (
          <NavCardRow
            key={workspace.slug}
            current={workspace.slug === ws}
            icon={<TeamMark name={workspace.name} theme={workspace.theme} logoUrl={workspace.logo_url} className="inline-flex size-(--sidebar-mark-size) items-center justify-center rounded-(--sidebar-mark-radius) bg-workspace-mark text-micro font-semibold text-chrome-canvas" />}
            label={workspace.name}
            trailing={workspace.slug === ws ? <Check /> : undefined}
            onSelect={act(() => {
              if (workspace.slug === ws) return;
              runtime.storage.set(lastWorkspaceKey, workspace.slug);
              void navigate(lastRouteFor(runtime, workspace.slug));
            })}
          />
        ))}
      </ul>
      <Separator className="my-(--space-2)" />
      <ul className="m-0 flex list-none flex-col gap-(--space-1) px-(--space-2)">
        {me.can_create_workspace && <NavCardRow icon={<Plus />} label={t("workspace.create.title")} onSelect={act(() => { void navigate("/new-workspace"); })} />}
        {isWorkspaceAdmin(me, ws) && <NavCardRow icon={<UserPlus />} label={t("nav.invitePeople")} onSelect={act(() => { void navigate(`/${ws}/settings/invites?invite=1`); })} />}
        <NavCardRow icon={<LogOut />} label={t("nav.signOut")} tone="danger" onSelect={act(() => { void signOut(runtime); })} />
      </ul>
    </Popover>
  );
}
